// useSourceEvents — shared lazy-fetch hook for the aggregation source-events
// blob attached to alerts and test-result rows. Lifted out of SourceEventsPanel
// in v1.60.0 so the new MailboxPairAllowlistFromAlertModal can reuse the same
// fetch without re-downloading the blob when the alert row is already expanded.
//
// Cache is module-scoped (one process-wide Map keyed by blobPath). It survives
// row collapse/expand and modal close/open. Cache never invalidates within a
// session — the blob content is immutable once written by the detection
// pipeline.

import { useEffect, useState } from 'react';
import { fetchAlertSourceEvents } from '../../api/client';

const cache = new Map(); // blobPath -> { promise?, data?, error? }

function normalizeBlobPath(blobPath) {
  if (!blobPath) return null;
  return String(blobPath).replace(/^blob:/, '');
}

export function useSourceEvents(blobPath, tenantId, alertId) {
  const url = normalizeBlobPath(blobPath);
  const [state, setState] = useState(() => {
    if (!url) return { loading: false, data: null, error: null };
    const cached = cache.get(url);
    if (cached?.data) return { loading: false, data: cached.data, error: null };
    if (cached?.error) return { loading: false, data: null, error: cached.error };
    return { loading: true, data: null, error: null };
  });

  useEffect(() => {
    if (!url) {
      setState({ loading: false, data: null, error: null });
      return;
    }
    const cached = cache.get(url);
    if (cached?.data) {
      setState({ loading: false, data: cached.data, error: null });
      return;
    }
    if (cached?.error) {
      setState({ loading: false, data: null, error: cached.error });
      return;
    }
    if (cached?.promise) {
      setState({ loading: true, data: null, error: null });
      cached.promise.then(
        (d) => setState({ loading: false, data: d, error: null }),
        (e) => setState({ loading: false, data: null, error: e?.message || 'Failed to load source events' })
      );
      return;
    }

    setState({ loading: true, data: null, error: null });
    const promise = fetchAlertSourceEvents(url, tenantId, alertId)
      .then((d) => {
        cache.set(url, { data: d });
        return d;
      })
      .catch((e) => {
        const msg = e?.message || 'Failed to load source events';
        cache.set(url, { error: msg });
        throw e;
      });
    cache.set(url, { promise });
    promise.then(
      (d) => setState({ loading: false, data: d, error: null }),
      (e) => setState({ loading: false, data: null, error: e?.message || 'Failed to load source events' })
    );
  }, [url, tenantId, alertId]);

  return state;
}
